// Game constants
export const COLORS = ['red', 'blue', 'green', 'yellow'];
export const VALUES = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', 'Skip', 'Reverse', 'Draw Two'];
export const WILD_CARDS = ['Wild', 'Wild Draw Four'];

// Create a complete Uno deck
export const createDeck = () => {
  const deck = [];
  
  // Add number cards and special cards (Skip, Reverse, Draw Two)
  COLORS.forEach(color => {
    // Add one '0' card for each color
    deck.push({ color, value: '0', type: 'number' });
    
    // Add two of each number 1-9 and special cards for each color
    for (let i = 1; i < VALUES.length; i++) {
      const value = VALUES[i];
      let type = 'number';
      
      if (value === 'Skip') type = 'skip';
      if (value === 'Reverse') type = 'reverse';
      if (value === 'Draw Two') type = 'draw-two';
      
      // Add two of each card
      deck.push({ color, value, type });
      deck.push({ color, value, type });
    }
  });
  
  // Add Wild cards and Wild Draw Four cards (4 of each)
  for (let i = 0; i < 4; i++) {
    deck.push({ color: null, value: 'Wild', type: 'wild' });
    deck.push({ color: null, value: 'Wild Draw Four', type: 'wild-draw-four' });
  }
  
  return deck;
};

// Shuffle the deck using Fisher-Yates algorithm
export const shuffleDeck = (deck) => {
  const shuffled = [...deck];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

// Deal cards to players
export const dealCards = (deck, numPlayers, cardsPerPlayer = 7) => {
  const hands = Array(numPlayers).fill().map(() => []);
  const newDeck = [...deck];
  
  // Deal cards one at a time to each player
  for (let i = 0; i < cardsPerPlayer; i++) {
    for (let j = 0; j < numPlayers; j++) {
      if (newDeck.length > 0) {
        hands[j].push(newDeck.pop());
      }
    }
  }
  
  return { hands, deck: newDeck };
};

// Check if a card can be played on top of another card
export const isValidPlay = (card, topCard, currentColor) => {
  // Wild cards can always be played
  if (card.type === 'wild' || card.type === 'wild-draw-four') {
    return true;
  }
  
  // If the top card is a wild card, check against the current color
  if (topCard.type === 'wild' || topCard.type === 'wild-draw-four') {
    return card.color === currentColor;
  }
  
  // Otherwise, check if the card matches the color or value of the top card
  return card.color === topCard.color || card.value === topCard.value;
};

// Get the next player index based on current direction
export const getNextPlayer = (currentPlayer, direction, numPlayers) => {
  return (currentPlayer + direction + numPlayers) % numPlayers;
};

// Initialize players (1 human, 3 computer)
export const initializePlayers = (hands) => {
  return hands.map((hand, index) => ({
    name: index === 0 ? 'You' : `Computer ${index}`,
    hand,
    isComputer: index !== 0,
  }));
};

// Handle special card effects
export const handleSpecialCard = (card, gameState) => {
  const { currentPlayer, direction, players } = gameState;
  
  // Always calculate the next player in the current direction
  let nextPlayer = getNextPlayer(currentPlayer, direction, players.length);
  let newDirection = direction;
  let skipNextPlayer = false;
  let drawCards = 0;
  let affectedPlayer = nextPlayer; // The player who will draw cards (always the next player)
  
  switch (card.type) {
    case 'skip':
      skipNextPlayer = true;
      break;
    case 'reverse':
      newDirection = -direction;
      // In a two-player game, reverse acts like skip
      if (players.length === 2) {
        skipNextPlayer = true;
      } else {
        // Recalculate next player with new direction
        nextPlayer = getNextPlayer(currentPlayer, newDirection, players.length);
      }
      break;
    case 'draw-two':
      drawCards = 2;
      skipNextPlayer = true;
      break;
    case 'wild-draw-four':
      drawCards = 4;
      skipNextPlayer = true;
      break;
    default:
      break;
  }
  
  // If we need to skip the next player, calculate the new next player
  if (skipNextPlayer) {
    nextPlayer = getNextPlayer(nextPlayer, newDirection, players.length);
  }
  
  return {
    nextPlayer,
    newDirection,
    drawCards,
    affectedPlayer, // Always the player immediately after the current player
  };
};
